import styled, { DefaultTheme, keyframes } from 'styled-components';

import SvgIcon from '~src/shared/SvgIcon';
import { media } from '~src/styles/media-templates';

import { ButtonColorTypes, buttonColors } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import { calcShadowList } from './scaleBoxItem/scaleBoxItem.sc';
import TextBoxWithTooltip from '~src/components/TextBoxWithTooltip/TextBoxWithTooltip';

export const ScaleDisplayContainer = styled.div<{ $isLast: boolean; $isCustomLabelsScale?: boolean }>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: 0.8rem;
  ${media.phone`
    padding: 10px;
    width: 100%;
  `}
  &.vertical {
    width: 100%;
    max-width: 280px; /* in mobile view this is limited by the card container to 300; minus margins its 280 */
  }
  &.withBackground {
    background: ${({ theme }) => theme.baseBlue.a10};
    padding: 2.5rem;
    border-radius: ${({ $isLast }) => ($isLast ? '0 0 5px 5px' : '0')};
  }
  ${({ $isCustomLabelsScale }) =>
    $isCustomLabelsScale &&
    `
    margin-top: 1.7rem;
  `}
`;

export const StyledTextBoxWithTooltip = styled(TextBoxWithTooltip)<{$cursorPointer: boolean}>`
  width: 200px;
  padding: 12px;
  text-align: center;
  cursor: ${({ $cursorPointer }) => ($cursorPointer ? 'pointer' : 'auto')};
`

const particleD = 0.6; // size of the bubbles
const particleR = 0.55 * particleD;

const bubblegrow = (theme: DefaultTheme) =>
  keyframes`
    0% {
      opacity: 0;
      box-shadow: ${calcShadowList(0, 'stars', particleD, particleR, theme.gold.a10)};
    }

    30% {
      opacity: 0;
      box-shadow: ${calcShadowList(0, 'stars', particleD, particleR, theme.gold.a10)};
    }

    100% {
      opacity: 1;
      box-shadow: ${calcShadowList(1, 'stars', particleD, particleR, theme.gold.a10)};
    }
  `;

const growStar = keyframes`
  0% {
    transform: scale(1);
  }

  20% {
    transform: scale(1.3);
  }

  40% {
    transform: scale(1);
  }
`;

export const StarBtn = styled.button.attrs({
  type: 'submit',
})<{
  selected: boolean;
  $animated: boolean;
}>`
  position: relative;
  font-size: 1.7rem;
  padding-left: 1rem;
  padding-right: 1rem;
  cursor: pointer;
  transition: all 0.3s ease-in;
  border: 0;
  outline: none;
  background: none;
  animation-duration: 1s;
  animation-timing-function: ease-out;
  animation-name: ${({ $animated }) => ($animated ? growStar : 'none')};
  ${media.phone`
    padding-left: 11px;
    padding-right: 11px;
  `}
  &:active {
    transform: scale(1.2);
  }

  &::after {
    animation: ${({ selected, $animated }) => (selected && $animated ? 'inherit' : 'none')};
    animation-name: ${({ selected, $animated, theme }) => (selected && $animated ? bubblegrow(theme) : 'none')};
    position: absolute;
    top: 50%;
    left: 50%;
    will-change: opacity, box-shadow;
    border-radius: 50%;
    display: ${({ selected, $animated }) => (selected && $animated ? 'block' : 'none')};
    transform: scale(1) rotate(45deg);
    content: '';
    z-index: 33;
    margin: -${particleR}rem;
    width: ${particleD}rem;
    height: ${particleD}rem;
    box-shadow: ${({ theme }) => calcShadowList(1, 'stars', particleD, particleR, theme.gold.a10)};
  }
`;

export const ScaleMarginContainer = styled.button`
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 4px;
  ${media.phone`
    margin: 5px 0;
    width: 100%;
  `}
  &.vertical {
    margin: 5px 0;
    width: 100%;
  }

  &:focus {
    border: none;
    outline: none;
  }

  &::selection {
    background: none;
  }
`;

export const ScaleBox = styled.div<{ $type: string }>`
  max-width: 730px;
  /* F*** IE11 🤬 */
  display: ${({ $type }) => ($type === 'nps' ? 'block' : 'flex')};
  position: relative;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  ${media.phone`
    display: flex;
    flex-direction: ${({ $type }) => ($type === 'stars' ? 'row' : 'column-reverse')};
    max-width: 100%;
    width: 100%;
    justify-content: center;
  `}
  &.vertical {
    display: flex;
    flex-direction: ${({ $type }) => ($type === 'stars' ? 'row' : 'column-reverse')};
    max-width: 100%;
    width: 100%;
    justify-content: center;
  }
`;

export const StarIcon = styled(SvgIcon)`
  svg {
    stroke: ${({ theme }) => theme.gold.a10};
    ${media.phone`
      height: 18px;
    `}
  }

  &.filled svg {
    fill: ${({ theme }) => theme.gold.a10};
  }
`;

export const McqOptionsSubmitButtonContainer = styled.div`
    display: flex;
    margin-top: 0.5rem;
    width: 100%;
    justify-content: center;
`;

export const McqOptionsSubmitButton = styled(PrimaryButton)`
  color: ${(props) =>
    props.$highlighted ? buttonColors[ButtonColorTypes.Blue] : buttonColors[ButtonColorTypes.White]};
  background-color: ${(props) =>
    props.$highlighted ? buttonColors[ButtonColorTypes.White] : buttonColors[ButtonColorTypes.Blue]};
`;
